import axios from 'axios'
import { authApiClient } from '@/lib/api-client'
import type {
  DealerResponse,
  EnetroUserDetailResponse,
  LoginRequest,
  LoginResponse,
  RefreshTokenRequest,
  RefreshTokenResponse,
} from '@/features/auth/types/auth'

const authPath =
  import.meta.env.VITE_AUTH_BASE_URL || '/internal/readywire-auth'
const restBase = import.meta.env.VITE_API_URL || 'https://api.dev.readywire.com'
const authBaseURL = `${restBase.replace(/\/$/, '')}${authPath}`
const apiKey = import.meta.env.VITE_X_API_KEY

const enetroDealerApiBaseUrl = import.meta.env
  .VITE_ENETRO_DEALER_API_BASE_URL as string | undefined
const enetroDealerApiRegion =
  (
    import.meta.env.VITE_ENETRO_DEALER_API_REGION as string | undefined
  )?.trim() || 'dev'

const refreshOnlyClient = axios.create({
  baseURL: authBaseURL,
  headers: {
    'Content-Type': 'application/json',
    'x-api-key': apiKey || '',
  },
})

const enetroDealerClient = axios.create({
  headers: {
    'Content-Type': 'application/json',
    ...(apiKey ? { 'x-api-key': apiKey } : {}),
  },
})

/**
 * Fetch dealer details by email.
 * When VITE_ENETRO_DEALER_API_BASE_URL is set: POST SMGR
 * `/dealer-info/check-admin/subscribers?region=…` (same response shape as legacy user_detail).
 */
export async function getDealerByEmail(
  email: string
): Promise<DealerResponse | null> {
  const encodedEmail = encodeURIComponent(email)
  const trimmedEmail = email.trim().replace(/\s+/g, '')

  if (enetroDealerApiBaseUrl) {
    try {
      const base = enetroDealerApiBaseUrl.replace(/\/$/, '')
      const response = await enetroDealerClient.post<EnetroUserDetailResponse>(
        `${base}/dealer-info/check-admin/subscribers`,
        { userName: trimmedEmail },
        { params: { region: enetroDealerApiRegion } }
      )
      const data = response.data
      const isSuperAdmin = data.is_super_admin === true
      const rawDealers = data.dealers ?? []
      const filtered = isSuperAdmin
        ? rawDealers
        : rawDealers.filter((d) => d.is_active === true)
      const dealers = filtered.map((d) => ({
        id: d.dealer_id,
        name: d.dealer_name,
      }))
      return {
        user_id: data.user_name ?? '',
        is_super_admin: isSuperAdmin,
        dealers: dealers.length === 0 ? null : dealers,
      }
    } catch (error: unknown) {
      if (
        error &&
        typeof error === 'object' &&
        'response' in error &&
        (error as { response?: { status?: number } }).response?.status === 404
      ) {
        return null
      }
      throw error
    }
  }

  try {
    const response = await authApiClient.get<DealerResponse>(
      `${import.meta.env.VITE_URL_PATH || '/internal/readywire-bulk'}/dealerid?email=${encodedEmail}`
    )
    return response.data
  } catch (error: unknown) {
    if (
      error &&
      typeof error === 'object' &&
      'response' in error &&
      (error as { response?: { status?: number } }).response?.status === 404
    ) {
      return null
    }
    throw error
  }
}

/**
 * Login user with credentials
 * @param credentials - Login credentials
 * @returns Login response with tokens and user data
 */
export async function login(credentials: LoginRequest): Promise<LoginResponse> {
  const response = await refreshOnlyClient.post<LoginResponse>(
    '/login',
    credentials
  )
  return response.data
}

/**
 * Refresh access token using refresh token.
 * Uses isolated client: does NOT attach access token, does NOT trigger 401 interceptors.
 * @param refreshData - Refresh token data
 * @returns Refresh token response with new tokens
 */
export async function refreshToken(
  refreshData: RefreshTokenRequest
): Promise<RefreshTokenResponse> {
  const response = await refreshOnlyClient.post<RefreshTokenResponse>(
    '/refresh-token',
    refreshData
  )
  return response.data
}
